import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

// Brand colors
const BRAND_BLUE = '#2B4B6A';

const QuestionManager = ({ category, title, description }) => {
  const [questions, setQuestions] = useState([]);
  const [processSteps, setProcessSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [formData, setFormData] = useState({
    question_text: '',
    field_type: 'text',
    question_category: category,
    step_id: '',
    options: [],
    optionsText: '',
    allow_multiple: false,
    display_order: 0,
    is_required: true,
    is_hidden: false,
    is_product_name: false
  });

  useEffect(() => {
    fetchData();
  }, [category]);

  const fetchData = async () => {
    try {
      const [questionsRes, stepsRes] = await Promise.all([
        api.get('/admin/questions'),
        api.get('/admin/quote-process/steps').catch(() => ({ data: [] }))
      ]);
      const filtered = questionsRes.data.filter(q => q.question_category === category);
      setQuestions(filtered);
      setProcessSteps(stepsRes.data);
      
      // Expand all sections by default
      const expanded = { 'unassigned': true };
      stepsRes.data.forEach(step => {
        expanded[step.id] = true;
      });
      setExpandedSections(expanded);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getFieldTypeLabel = (fieldType) => {
    const labels = {
      'text': 'Single Line Text',
      'textarea': 'Paragraph Text',
      'number': 'Number',
      'email': 'Email',
      'phone': 'Phone',
      'date': 'Date',
      'price': 'Price (USD)',
      'select': 'Dropdown',
      'checkbox': 'Checkboxes',
      'note': 'Notes / Instructions'
    };
    return labels[fieldType] || fieldType;
  };

  // Group questions by step
  const getGroupedQuestions = useCallback(() => {
    const groups = {};
    
    // Initialize groups for each step
    processSteps.forEach(step => {
      groups[step.id] = {
        step: step,
        questions: []
      };
    });
    
    // Add unassigned group
    groups['unassigned'] = {
      step: null,
      questions: []
    };
    
    // Distribute questions to groups
    questions.forEach(q => {
      const stepId = q.step_id || 'unassigned';
      if (groups[stepId]) {
        groups[stepId].questions.push(q);
      } else {
        groups['unassigned'].questions.push(q);
      }
    });
    
    // Sort questions within each group by display_order
    Object.values(groups).forEach(group => {
      group.questions.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    });
    
    return groups;
  }, [questions, processSteps]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Drag and drop handlers
  const handleDragStart = (e, question, sectionId) => {
    setDraggedItem({ question, sectionId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, question, sectionId) => {
    e.preventDefault();
    if (draggedItem && draggedItem.sectionId === sectionId && draggedItem.question.id !== question.id) {
      setDragOverItem({ question, sectionId });
    }
  };

  const handleDragEnd = async () => {
    if (!draggedItem || !dragOverItem || draggedItem.sectionId !== dragOverItem.sectionId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const sectionId = draggedItem.sectionId;
    const groups = getGroupedQuestions();
    const sectionQuestions = [...groups[sectionId].questions];
    
    const dragIndex = sectionQuestions.findIndex(q => q.id === draggedItem.question.id);
    const dropIndex = sectionQuestions.findIndex(q => q.id === dragOverItem.question.id);
    
    if (dragIndex === -1 || dropIndex === -1) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Reorder the array
    const [removed] = sectionQuestions.splice(dragIndex, 1);
    sectionQuestions.splice(dropIndex, 0, removed);

    // Update display_order for all questions in section
    const reorderItems = sectionQuestions.map((q, idx) => ({
      id: q.id,
      display_order: idx + 1
    }));

    // Optimistically update UI
    const updatedQuestions = questions.map(q => {
      const reorderItem = reorderItems.find(r => r.id === q.id);
      if (reorderItem) {
        return { ...q, display_order: reorderItem.display_order };
      }
      return q;
    });
    setQuestions(updatedQuestions);

    setDraggedItem(null);
    setDragOverItem(null);

    // Save to backend
    try {
      await api.post('/admin/questions/reorder', { items: reorderItems });
      toast.success('Questions reordered');
    } catch (error) {
      toast.error('Failed to save order');
      fetchData(); // Revert on error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const optionsArray = formData.optionsText 
      ? formData.optionsText.split(',').map(o => o.trim()).filter(o => o)
      : [];
    
    try {
      if (editingQuestion) {
        await api.put(`/admin/questions/${editingQuestion.id}`, {
          ...formData,
          options: optionsArray.length > 0 ? optionsArray : null,
          step_id: formData.step_id || null,
          allow_multiple: formData.allow_multiple || false,
          is_product_name: formData.is_product_name || false
        });
        toast.success('Question updated successfully');
      } else {
        // Get max display_order for the selected step
        const stepQuestions = questions.filter(q => (q.step_id || '') === (formData.step_id || ''));
        const maxOrder = stepQuestions.length > 0 
          ? Math.max(...stepQuestions.map(q => q.display_order || 0)) 
          : 0;
        
        await api.post('/admin/questions', {
          ...formData,
          options: optionsArray.length > 0 ? optionsArray : null,
          display_order: maxOrder + 1,
          step_id: formData.step_id || null,
          allow_multiple: formData.allow_multiple || false,
          is_product_name: formData.is_product_name || false
        });
        toast.success('Question created successfully');
      }
      
      setShowDialog(false);
      setEditingQuestion(null);
      fetchData();
      setFormData({ question_text: '', field_type: 'text', question_category: category, step_id: '', options: [], optionsText: '', allow_multiple: false, display_order: 0, is_required: true, is_hidden: false, is_product_name: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingQuestion ? 'Failed to update question' : 'Failed to create question'));
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    const optionsArr = question.options || [];
    setFormData({
      question_text: question.question_text,
      field_type: question.field_type,
      question_category: question.question_category,
      step_id: question.step_id || '',
      options: optionsArr,
      optionsText: optionsArr.join(', '),
      allow_multiple: question.allow_multiple || false,
      display_order: question.display_order,
      is_required: question.is_required === 1 || question.is_required === true,
      is_hidden: question.is_hidden === 1 || question.is_hidden === true,
      is_product_name: question.is_product_name || false
    });
    setShowDialog(true);
  };

  const handleDialogClose = (open) => {
    if (!open) {
      setEditingQuestion(null);
      setFormData({ question_text: '', field_type: 'text', question_category: category, step_id: '', options: [], optionsText: '', allow_multiple: false, display_order: 0, is_required: true, is_hidden: false, is_product_name: false });
    }
    setShowDialog(open);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await api.delete(`/admin/questions/${id}`);
      toast.success('Question deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleAddToStep = (stepId) => {
    setFormData(prev => ({ ...prev, step_id: stepId }));
    setShowDialog(true);
  };

  // Render a question item
  const renderQuestionItem = (q, idx, sectionId) => {
    const isDragging = draggedItem?.question.id === q.id;
    const isDragOver = dragOverItem?.question.id === q.id;
    
    return (
      <div
        key={q.id}
        data-testid={`question-${q.id}`}
        draggable
        onDragStart={(e) => handleDragStart(e, q, sectionId)}
        onDragOver={(e) => handleDragOver(e, q, sectionId)}
        onDragEnd={handleDragEnd}
        className={`p-4 flex items-center gap-3 bg-white border border-gray-100 rounded-lg mb-2 transition-all cursor-move
          ${isDragging ? 'opacity-50 scale-95' : ''}
          ${isDragOver ? 'border-blue-400 bg-blue-50' : 'hover:border-gray-200 hover:shadow-sm'}
        `}
      >
        <div className="text-gray-300 hover:text-gray-500 cursor-grab">
          <GripVertical className="w-5 h-5" />
        </div>
        
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-mono text-gray-400">#{idx + 1}</span>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{getFieldTypeLabel(q.field_type)}</span>
            {q.is_product_name && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">Product Name</span>}
            {q.field_type === 'note' && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Display Only</span>}
            {q.allow_multiple && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">Multi-select</span>}
            {q.field_type !== 'note' && (q.is_required === 0 || q.is_required === false) && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Optional</span>
            )}
            {(q.is_hidden === 1 || q.is_hidden === true) && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Hidden</span>
            )}
          </div>
          <div 
            className="text-gray-800 text-sm font-medium break-words whitespace-pre-wrap [&_a]:text-blue-600 [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: q.question_text }}
          />
          {q.options && Array.isArray(q.options) && q.options.length > 0 && (
            <p className="text-xs text-gray-400 mt-0.5 break-words">
              Options: {q.options.join(', ')}
            </p>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(q); }}
            className="text-gray-400 hover:text-blue-600 p-1.5 hover:bg-blue-50 rounded-lg transition"
            title="Edit question"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(q.id); }}
            data-testid={`delete-question-${q.id}`}
            className="text-gray-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition"
            title="Delete question"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  // Render a section (step group)
  const renderSection = (sectionId, group) => {
    const isExpanded = expandedSections[sectionId] !== false;
    const stepName = group.step ? `Step ${group.step.order_number}: ${group.step.name}` : 'Unassigned Questions';
    const questionCount = group.questions.length;
    
    return (
      <div key={sectionId} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-4">
        {/* Section Header */}
        <div 
          className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition"
          onClick={() => toggleSection(sectionId)}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <h3 className="font-semibold text-gray-800">{stepName}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {questionCount} question{questionCount !== 1 ? 's' : ''}
                {questionCount > 1 && ' • Drag to reorder'}
              </p>
            </div>
          </div>
          
          <button
            onClick={(e) => { e.stopPropagation(); handleAddToStep(sectionId === 'unassigned' ? '' : sectionId); }}
            className="text-sm px-3 py-1.5 rounded-lg font-medium transition flex items-center gap-1 text-white"
            style={{ backgroundColor: BRAND_BLUE }}
            data-testid={`add-to-step-${sectionId}`}
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
        
        {/* Section Content */}
        {isExpanded && (
          <div className="px-5 pb-4">
            {questionCount > 0 ? (
              <div className="space-y-0">
                {group.questions.map((q, idx) => renderQuestionItem(q, idx, sectionId))}
              </div>
            ) : (
              <div className="py-6 text-center text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                No questions in this step yet
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  const groupedQuestions = getGroupedQuestions();
  
  // Order sections: steps by order_number, then unassigned at the end
  const orderedSections = [
    ...processSteps.map(step => ({ id: step.id, group: groupedQuestions[step.id] })),
    { id: 'unassigned', group: groupedQuestions['unassigned'] }
  ];

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
            <p className="text-gray-500 text-sm mt-1">{description}</p>
          </div>
          <button
            onClick={() => setShowDialog(true)}
            data-testid="add-question-button"
            className="text-white px-4 py-2.5 rounded-xl font-medium inline-flex items-center gap-2 transition hover:opacity-90"
            style={{ backgroundColor: BRAND_BLUE }}
          >
            <Plus className="w-4 h-4" />
            Add Question
          </button>
        </div>

        {/* Sections grouped by step */}
        {orderedSections.map(({ id, group }) => renderSection(id, group))}

        {/* Dialog for adding/editing questions */}
        <Dialog open={showDialog} onOpenChange={handleDialogClose}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add New Question'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Question Text</label>
                <input
                  data-testid="question-text-input"
                  type="text"
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                />
              </div>

              {/* Process Step Assignment */}
              {category === 'loi' && processSteps.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Process Step</label>
                  <select
                    value={formData.step_id}
                    onChange={(e) => setFormData({ ...formData, step_id: e.target.value, is_product_name: false })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                    data-testid="step-select"
                  >
                    <option value="">-- No step (general question) --</option>
                    {processSteps.map(step => (
                      <option key={step.id} value={step.id}>
                        Step {step.order_number}: {step.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Questions assigned to a step will only appear on that step</p>
                </div>
              )}

              {/* Product Name Question Checkbox - Only show for Step 1 */}
              {category === 'loi' && formData.step_id && processSteps.find(s => s.id === formData.step_id)?.order_number === 1 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_product_name || false}
                      onChange={(e) => setFormData({ ...formData, is_product_name: e.target.checked })}
                      className="w-5 h-5 rounded border-amber-400"
                      style={{ accentColor: '#D97706' }}
                      data-testid="is-product-name-checkbox"
                    />
                    <div>
                      <span className="text-sm font-semibold text-amber-800">Is this the product name question?</span>
                      <p className="text-xs text-amber-600 mt-0.5">
                        The answer to this question will be displayed as the Product Name in quote request lists.
                        Only one question in Step 1 can be marked as the product name.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Type</label>
                <select
                  data-testid="field-type-select"
                  value={formData.field_type}
                  onChange={(e) => setFormData({ ...formData, field_type: e.target.value, allow_multiple: false })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                >
                  <option value="text">Single Line Text</option>
                  <option value="textarea">Paragraph Text</option>
                  <option value="number">Number</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="date">Date</option>
                  <option value="price">Price (USD)</option>
                  <option value="select">Dropdown</option>
                  <option value="checkbox">Checkboxes</option>
                  <option value="note">Notes / Instructions (Display Only)</option>
                </select>
              </div>

              {/* Help text for Notes field type */}
              {formData.field_type === 'note' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <p className="text-sm text-blue-700">
                    <strong>Notes field:</strong> This will display as formatted text/instructions to users. 
                    Users cannot input anything - it's for displaying information, instructions, or descriptions between questions.
                  </p>
                </div>
              )}

              {/* Options for Select/Dropdown and Checkboxes */}
              {(formData.field_type === 'select' || formData.field_type === 'checkbox') && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Options (comma-separated)
                    </label>
                    <input
                      data-testid="options-input"
                      type="text"
                      value={formData.optionsText}
                      onChange={(e) => setFormData({ ...formData, optionsText: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                      placeholder="Option 1, Option 2, Option 3"
                    />
                    <p className="text-xs text-gray-500 mt-1">Separate each option with a comma</p>
                  </div>
                  
                  {formData.field_type === 'select' && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allow_multiple || false}
                        onChange={(e) => setFormData({ ...formData, allow_multiple: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300"
                        style={{ accentColor: BRAND_BLUE }}
                      />
                      <span className="text-sm font-medium text-gray-700">Allow multiple selections</span>
                    </label>
                  )}
                </div>
              )}

              {/* Hide these options for Notes field type since it's display-only */}
              {formData.field_type !== 'note' && (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_hidden}
                      onChange={(e) => {
                        const isHidden = e.target.checked;
                        setFormData({ 
                          ...formData, 
                          is_hidden: isHidden,
                          is_required: isHidden ? false : formData.is_required
                        });
                      }}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-600"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Hidden field (admin only)
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 pl-6">Hidden fields are only visible to admins</p>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_required}
                      disabled={formData.is_hidden}
                      onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                      className="w-4 h-4 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ accentColor: BRAND_BLUE }}
                    />
                    <span className={`text-sm font-medium ${formData.is_hidden ? 'text-gray-400' : 'text-gray-700'}`}>
                      Required field {formData.is_hidden && '(disabled for hidden)'}
                    </span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                data-testid="submit-question-button"
                className="w-full text-white py-2.5 px-4 rounded-xl font-medium transition"
                style={{ backgroundColor: BRAND_BLUE }}
              >
                {editingQuestion ? 'Update Question' : 'Create Question'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
};

export default QuestionManager;
